import React, { useState, useEffect } from 'react';
import CampaignCreator from './components/CampaignCreator';
import { Send, Users, BarChart3, Settings, MessageSquare, Clock, CheckCircle, TrendingUp, Edit, Trash2, Plus, Save, User, Shield, Database, Check, X, Search, UserCheck, Zap, Target, Filter, Calendar, LogOut, Moon, Sun } from 'lucide-react';
import CampaignTracker from './components/CampaignTracker';
import { dashboardAPI, contactsAPI, campaignsAPI, analyticsAPI, settingsAPI, executivesAPI } from './services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

// 🆕 NUEVOS COMPONENTES AVANZADOS
// Componentes avanzados obsoletos removidos
import InlineCampaignImporter from './components/campaigns/InlineCampaignImporter';
import { useCampaignProgress } from './hooks/useCampaignProgress';
import Login from './components/Login';

// ==========================================
// WHATSAPP API INTEGRATION - VERSIÓN COMPLETA
// ==========================================
const whatsappAPI = {
  getStatus: async () => {
    const response = await fetch('/sistemasms/backend/api/whatsapp/status.php', {
      credentials: 'include'
    });
    return await response.json();
  },
  
  sendTest: async (phone, message) => {
    const response = await fetch('/sistemasms/backend/api/whatsapp/test.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phone, message })
    });
    return await response.json();
  },
  
  sendCampaign: async (campaignId) => {
    const response = await fetch('/sistemasms/backend/api/whatsapp/send-campaign.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ campaign_id: campaignId })
    });
    return response;
  },

  verifyNumbers: async (campaignId) => {
    const response = await fetch('/sistemasms/backend/api/whatsapp/verify-numbers.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ campaign_id: campaignId })
    });
    return await response.json();
  },

  checkSingleNumber: async (phone) => {
    const response = await fetch('/sistemasms/backend/api/whatsapp/check-single-number.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phone: phone })
    });
    return await response.json();
  }
};





// ==========================================
// NUEVO COMPONENTE: ENVÍO MASIVO DIRECTO
// ==========================================
const BulkWhatsAppSender = () => {

  const [selectedContacts, setSelectedContacts] = useState([
    // Contactos de prueba para ejecutivo
    { id: 1, full_name: 'Juan Pérez', phone: '+56912345678', email: 'juan@email.com', group_name: 'Clientes' },
    { id: 2, full_name: 'María González', phone: '+56987654321', email: 'maria@email.com', group_name: 'Clientes' },
    { id: 3, full_name: 'Carlos López', phone: '+56911223344', email: 'carlos@email.com', group_name: 'Prospectos' },
  ]);
  const [message, setMessage] = useState('Hola {nombre}, gracias por tu interés en nuestros servicios.');
  const [sendDelay, setSendDelay] = useState(5);
  const [sending, setSending] = useState(false);
  const [sendLogs, setSendLogs] = useState([]);
  const [progress, setProgress] = useState({ sent: 0, total: 0, failed: 0 });

  const removeContact = (contactId) => {
    setSelectedContacts(prev => prev.filter(c => c.id !== contactId));
  };

  const sendBulkMessages = async () => {
    if (!message.trim()) {
      alert('Por favor escribe un mensaje para enviar');
      return;
    }

    if (!window.confirm(`¿Enviar mensaje a ${selectedContacts.length} contactos?`)) {
      return;
    }

    setSending(true);
    setSendLogs([]);
    setProgress({ sent: 0, total: selectedContacts.length, failed: 0 });

    try {
      for (let i = 0; i < selectedContacts.length; i++) {
        const contact = selectedContacts[i];
        
        try {
          const personalizedMessage = message.replace(
            /{nombre}|{name}/gi, 
            contact.full_name
          );

          const result = await whatsappAPI.sendTest(contact.phone, personalizedMessage);

          if (result.success) {
            setProgress(prev => ({ ...prev, sent: prev.sent + 1 }));
            setSendLogs(prev => [...prev, 
              `✅ ${i + 1}/${selectedContacts.length} - Enviado a ${contact.full_name}`
            ]);
          } else {
            throw new Error(result.error || 'Error desconocido');
          }

        } catch (error) {
          setProgress(prev => ({ ...prev, failed: prev.failed + 1 }));
          setSendLogs(prev => [...prev, 
            `❌ ${i + 1}/${selectedContacts.length} - Error enviando a ${contact.full_name}: ${error.message}`
          ]);
        }

        if (i < selectedContacts.length - 1) {
          setSendLogs(prev => [...prev, `⏳ Esperando ${sendDelay} segundos...`]);
          await new Promise(resolve => setTimeout(resolve, sendDelay * 1000));
        }
      }

      setSendLogs(prev => [...prev, 
        `\n🎉 ENVÍO COMPLETADO!`,
        `📊 Resumen: ${progress.sent} enviados, ${progress.failed} fallidos`
      ]);

    } catch (error) {
      setSendLogs(prev => [...prev, `❌ ERROR GENERAL: ${error.message}`]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">📱 Envío Masivo WhatsApp</h2>
          <p className="text-gray-600">Envía mensajes personalizados a múltiples contactos</p>
        </div>
        <div className="text-sm text-green-600 bg-green-100 px-3 py-1 rounded-full">
          WhatsApp Conectado
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Destinatarios ({selectedContacts.length})
          </h3>
          <div className="text-sm text-gray-500">
            Contactos pre-configurados para envío masivo
          </div>
        </div>

        {selectedContacts.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {selectedContacts.map(contact => (
              <div key={contact.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-800">{contact.full_name}</h4>
                  <p className="text-sm text-gray-600">{contact.phone}</p>
                </div>
                <button
                  onClick={() => removeContact(contact.id)}
                  className="text-red-600 hover:text-red-800 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Users size={48} className="mx-auto mb-4 text-gray-300" />
            <p>No hay contactos seleccionados</p>
            <p className="text-sm">Haz clic en "Seleccionar Contactos" para comenzar</p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Configurar Mensaje</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mensaje
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows="6"
              placeholder="Escribe tu mensaje aquí...&#10;&#10;Puedes usar {nombre} para personalizar el mensaje"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              💡 Usa <code>{'{nombre}'}</code> para insertar el nombre del contacto automáticamente
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delay entre mensajes (segundos)
              </label>
              <input
                type="number"
                value={sendDelay}
                onChange={(e) => setSendDelay(parseInt(e.target.value))}
                min="1"
                max="60"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">Recomendado: 5-10 segundos</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tiempo estimado
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
                <span className="text-gray-700">
                  {selectedContacts.length > 0 
                    ? `~${Math.ceil((selectedContacts.length * sendDelay) / 60)} minutos`
                    : '0 minutos'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {sending && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Progreso del Envío</h3>
          
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Progreso</span>
              <span className="text-sm text-gray-500">
                {progress.sent} de {progress.total} ({progress.failed} fallidos)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((progress.sent + progress.failed) / progress.total) * 100}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg h-48 overflow-y-auto text-sm font-mono">
            {sendLogs.map((log, index) => (
              <div key={index} className={`mb-1 ${
                log.includes('✅') ? 'text-green-400' :
                log.includes('❌') ? 'text-red-400' :
                log.includes('⏳') ? 'text-yellow-400' :
                log.includes('📊') ? 'text-blue-400' :
                'text-gray-300'
              }`}>
                {log}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-center">
        <button
          onClick={sendBulkMessages}
          disabled={sending || selectedContacts.length === 0 || !message.trim()}
          className={`flex items-center gap-3 px-8 py-4 rounded-lg font-medium text-lg ${
            sending || selectedContacts.length === 0 || !message.trim()
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
          }`}
        >
          {sending ? (
            <>
              <div className="animate-spin rounded-full w-5 h-5 border-2 border-white border-t-transparent"></div>
              Enviando mensajes...
            </>
          ) : (
            <>
              <Zap size={20} />
              Enviar a {selectedContacts.length} contactos
            </>
          )}
        </button>
      </div>

    </div>
  );
};

// ==========================================
// COMPONENTE IMPORTACIÓN DE CONTACTOS PARA CAMPAÑAS
// ==========================================
const CampaignImportModal = ({ isOpen, onClose, campaignId, campaignName, onImportSuccess }) => {
  const [file, setFile] = useState(null);
  const [groupId, setGroupId] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      
      if (allowedTypes.includes(selectedFile.type) || selectedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
        setFile(selectedFile);
        setImportResult(null);
      } else {
        alert('Por favor selecciona un archivo Excel (.xlsx, .xls) o CSV');
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      alert('Por favor selecciona un archivo');
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('excel_file', file);
      formData.append('campaign_id', campaignId);
      if (groupId) {
        formData.append('group_id', groupId);
      }

      console.log('🚀 Importando contactos a campaña:', campaignId);

      const response = await fetch('/sistemasms/backend/api/campaigns/import-contacts.php', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const text = await response.text();
      console.log('📋 Response:', text);

      let result;
      try {
        result = JSON.parse(text);
      } catch (parseError) {
        throw new Error(`Respuesta inválida del servidor: ${text.substring(0, 100)}`);
      }

      if (!response.ok) {
        throw new Error(result.message || `Error HTTP ${response.status}`);
      }

      console.log('✅ Importación exitosa:', result);
      setImportResult(result);
      
      if (onImportSuccess && result.success) {
        onImportSuccess(result.data);
      }

    } catch (error) {
      console.error('💥 Error en importación:', error);
      setImportResult({
        success: false,
        message: error.message
      });
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-800">📤 Importar Contactos</h3>
              <p className="text-gray-600">Campaña: <strong>{campaignName}</strong> (ID: {campaignId})</p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">📋 Formato requerido:</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">nombre</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">telefono</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">email</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">empresa</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">rut</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-2 px-3 text-gray-600">Juan Pérez</td>
                    <td className="py-2 px-3 text-gray-600">+56912345678</td>
                    <td className="py-2 px-3 text-gray-600">juan@email.com</td>
                    <td className="py-2 px-3 text-gray-600">Empresa ABC</td>
                    <td className="py-2 px-3 text-gray-600">12345678-9</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              ⚠️ <strong>nombre</strong> y <strong>telefono</strong> son obligatorios. Las demás columnas son opcionales.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📁 Seleccionar archivo:
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              disabled={isImporting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {file && (
              <p className="text-sm text-green-600 mt-2">
                ✅ Archivo seleccionado: <strong>{file.name}</strong>
              </p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              👥 Grupo (opcional):
            </label>
            <input
              type="number"
              placeholder="ID del grupo (opcional)"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              disabled={isImporting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {importResult && (
            <div className={`mb-4 p-4 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <h4 className={`font-semibold ${importResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {importResult.success ? '✅ Importación Exitosa' : '❌ Error en Importación'}
              </h4>
              <p className={importResult.success ? 'text-green-700' : 'text-red-700'}>
                {importResult.message}
              </p>
              
              {importResult.success && importResult.data && (
                <div className="mt-3 text-sm text-green-700">
                  <p><strong>📊 Resultados:</strong></p>
                  <p>• Total filas: {importResult.data.total_rows}</p>
                  <p>• Importados: {importResult.data.successful_imports}</p>
                  <p>• Fallidos: {importResult.data.failed_imports}</p>
                  
                  {importResult.data.errors && importResult.data.errors.length > 0 && (
                    <div className="mt-2">
                      <p><strong>⚠️ Errores:</strong></p>
                      <ul className="list-disc list-inside">
                        {importResult.data.errors.map((error, index) => (
                          <li key={index} className="text-xs">{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex gap-3">
            <button
              onClick={handleImport}
              disabled={!file || isImporting}
              className={`flex-1 px-6 py-3 rounded-lg font-medium ${
                !file || isImporting
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isImporting ? (
                <>
                  <div className="inline-block animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Importando...
                </>
              ) : (
                '📤 Importar Contactos'
              )}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// COMPONENTE CAMPAIGN SENDER
// ==========================================
const WhatsAppCampaignSender = ({ campaign, onClose, onComplete }) => {
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState({ sent: 0, total: 0 });

  const sendCampaign = async () => {
    setSending(true);
    setLogs([]);
    setProgress({ sent: 0, total: 0 });

    try {
      const response = await whatsappAPI.sendCampaign(campaign.id);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.trim()) {
            setLogs(prev => [...prev.slice(-100), line]);
            
            const progressMatch = line.match(/(\d+)\/(\d+)/);
            if (progressMatch) {
              setProgress({
                sent: parseInt(progressMatch[1]),
                total: parseInt(progressMatch[2])
              });
            }
          }
        }
      }

      alert('🎉 ¡Campaña enviada exitosamente!');
      if (onComplete) onComplete();

    } catch (error) {
      console.error('Error enviando campaña:', error);
      alert('❌ Error enviando campaña: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-800">📱 Envío por WhatsApp</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-gray-800">{campaign.campaign_name}</h4>
          <p className="text-sm text-gray-600 mt-1">
            {campaign.message_content?.substring(0, 150)}...
          </p>
          <p className="text-sm text-gray-500 mt-2">👥 {campaign.total_recipients || 0} destinatarios</p>
        </div>

        {sending && progress.total > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Progreso</span>
              <span className="text-sm text-gray-500">{progress.sent} de {progress.total}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(progress.sent / progress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {logs.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium text-gray-800 mb-2">📋 Progreso:</h4>
            <div className="bg-gray-900 text-gray-100 p-4 rounded-lg h-48 overflow-y-auto text-sm font-mono">
              {logs.map((log, index) => (
                <div key={index} className={`mb-1 ${
                  log.includes('✅') ? 'text-green-400' :
                  log.includes('❌') ? 'text-red-400' :
                  log.includes('📊') ? 'text-blue-400' :
                  'text-gray-300'
                }`}>
                  {log}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={sendCampaign}
            disabled={sending}
            className={`flex-1 px-6 py-3 rounded-lg font-medium ${
              sending
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {sending ? (
              <>
                <div className="inline-block animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Enviando...
              </>
            ) : (
              '🚀 Enviar por WhatsApp'
            )}
          </button>
          
          <button
            onClick={onClose}
            className="px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};







// Componente para gestionar Usuarios Ejecutivos (crear/editar/borrar/habilitar)
const ExecutiveUsersManager = ({ executives, setExecutives }) => {
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', full_name: '', email: '', password: '', enabled: true });

  // Cargar al montar para asegurarnos que la lista se puebla si el padre no lo hizo
  useEffect(() => {
    // Solo si no hay ejecutivos cargados, realizar reload
    if (!executives || executives.length === 0) {
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const response = await executivesAPI.list();
      const raw = response.data?.executives || response.data?.data || response.data || [];
      const arr = Array.isArray(raw) ? raw : (raw.executives || []);
      // Deduplicate by id in case backend or localStorage returned duplicates
      const map = new Map();
      arr.forEach(item => {
        const id = item.id ?? item.ID ?? item.user_id ?? null;
        if (id !== null && id !== undefined) {
          map.set(Number(id), { ...item, id: Number(id) });
        }
      });
      const deduped = Array.from(map.values());
      setExecutives(deduped);
    } catch (error) {
      console.error('Error reload executives:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ username: '', full_name: '', email: '', password: '', enabled: true });
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ username: item.username || '', full_name: item.full_name || '', email: item.email || '', password: '', enabled: !!item.enabled });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const res = await executivesAPI.update(editing.id, form);
        if (res.data && res.data.success) {
          setExecutives(prev => prev.map(p => p.id === editing.id ? { ...p, ...form } : p));
        }
      } else {
        // validate password on create
        if (!form.password) {
          alert('La contraseña es requerida al crear un usuario');
          return;
        }
        const res = await executivesAPI.create(form);
        const newExec = res.data?.executive || res.data?.data || res.data;
        if (newExec) {
          setExecutives(prev => [ ...prev, newExec ]);
        }
      }
      setShowForm(false);
    } catch (error) {
      console.error('Error saving executive:', error);
      alert('Error guardando usuario: ' + (error.message || error));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Borrar este usuario ejecutivo? Esta acción no se puede deshacer.')) return;
    try {
      await executivesAPI.delete(id);
      setExecutives(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting executive:', error);
      alert('Error borrando usuario: ' + (error.message || error));
    }
  };

  const handleToggle = async (id, enabled) => {
    try {
      await executivesAPI.toggleEnabled(id, enabled);
      setExecutives(prev => prev.map(p => p.id === id ? { ...p, enabled } : p));
    } catch (error) {
      console.error('Error toggling executive:', error);
      alert('Error actualizando estado: ' + (error.message || error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold">👥 Usuarios Ejecutivos</h3>
          <p className="text-sm text-gray-500">Gestiona los usuarios ejecutivos: crear, editar, borrar y habilitar/deshabilitar.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reload} className="px-3 py-2 bg-gray-100 rounded-md">🔄 Recargar</button>
          <button onClick={openCreate} className="px-4 py-2 bg-blue-600 text-white rounded-md">➕ Crear Ejecutivo</button>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4">
        {loading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : (
          <div className="overflow-x-auto">
            {/* DEBUG: mostrar contenido de executives en UI para depuración temporal */}
            <div className="mb-4 p-3 bg-gray-50 border rounded text-xs text-gray-600">
              <strong>DEBUG executives (length):</strong> {Array.isArray(executives) ? executives.length : 'n/a'}
              <pre className="text-xs mt-2 overflow-x-auto">{JSON.stringify(executives, null, 2)}</pre>
            </div>
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500">
                  <th className="px-3 py-2">Usuario</th>
                  <th className="px-3 py-2">Nombre</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Habilitado</th>
                  <th className="px-3 py-2">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {(executives || []).map((exec) => (
                  <tr key={exec.id} className="border-t">
                    <td className="px-3 py-3 text-sm">{exec.username || exec.email || `u${exec.id}`}</td>
                    <td className="px-3 py-3 text-sm">{exec.full_name || '-'}</td>
                    <td className="px-3 py-3 text-sm">{exec.email || '-'}</td>
                    <td className="px-3 py-3 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={!!exec.enabled} onChange={(e) => handleToggle(exec.id, e.target.checked)} />
                        <span className="text-xs">{exec.enabled ? 'Sí' : 'No'}</span>
                      </label>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(exec)} className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">Editar</button>
                        <button onClick={() => handleDelete(exec.id)} className="px-2 py-1 bg-red-100 text-red-800 rounded">Borrar</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!executives || executives.length === 0) && (
                  <tr>
                    <td colSpan={5} className="text-center py-6 text-gray-500">No hay usuarios ejecutivos</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-bold">{editing ? 'Editar Ejecutivo' : 'Crear Ejecutivo'}</h4>
              <button onClick={() => setShowForm(false)} className="text-gray-500">✕</button>
            </div>

            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600">Usuario (username)</label>
                <input className="w-full px-3 py-2 border rounded" value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm text-gray-600">Nombre completo</label>
                <input className="w-full px-3 py-2 border rounded" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm text-gray-600">Email</label>
                <input type="email" className="w-full px-3 py-2 border rounded" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm text-gray-600">Contraseña {editing ? <span className="text-xs text-gray-400">(dejar en blanco para no modificar)</span> : null}</label>
                <input type="password" className="w-full px-3 py-2 border rounded" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder={editing ? '•••••• (opcional)' : ''} />
              </div>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({...form, enabled: e.target.checked})} />
                  <span className="text-sm">Habilitado</span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 bg-gray-200 rounded">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};


const MessageHub = () => {
  // Estados de autenticación
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Dark mode state with localStorage persistence
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('messagehub-theme');
    return saved === 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('messagehub-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const [activeTab, setActiveTab] = useState('bulk-sender');
  const [stats, setStats] = useState({
    total_contacts: 0,
    messages_sent: 0,
    delivery_rate: 0,
    active_campaigns: 0
  });
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados para Analytics
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('30');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Estados para Settings
  const [settingsData, setSettingsData] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [newGroupForm, setNewGroupForm] = useState({ group_name: '', description: '' });
  const [showNewGroupForm, setShowNewGroupForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
    const [showPasswordForm, setShowPasswordForm] = useState(false);

  // 🆕 Estados para el nuevo Dashboard con filtros
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardFilters, setDashboardFilters] = useState({
    empresa_id: 'all',
    period: 'week',
    start_date: '',
    end_date: ''
  });

  // 🆕 Estados para KPI de Cancelaciones
  const [kpiData, setKpiData] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [showKpiImport, setShowKpiImport] = useState(false);
  const [kpiPeriod, setKpiPeriod] = useState('month');
  const [kpiEmpresaId, setKpiEmpresaId] = useState('all');

    // Estados para campañas
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [importingFile, setImportingFile] = useState(false);
    const [showCampaignTracker, setShowCampaignTracker] = useState(false);

    // Función para verificar si una campaña tiene configuración avanzada guardada
    const hasAdvancedConfig = (campaignId) => {
      const configKey = `campaign_config_${campaignId}`;
      const savedConfig = localStorage.getItem(configKey);
      if (savedConfig) {
        try {
          const parsed = JSON.parse(savedConfig);
          return parsed.selectedSessions && parsed.selectedSessions.length > 0;
        } catch (error) {
          return false;
        }
      }
      return false;
    };
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [campaignForm, setCampaignForm] = useState({
    campaign_name: '',
    message_content: '',      // ✅ CAMBIADO: de message_template a message_content
    empresa_id: '',
    is_scheduled: false,
    send_delay_seconds: 5,
    scheduled_at: ''
  });

  // Estado para lista de campañas
  const [campaignsList, setCampaignsList] = useState([]);
  const [campaignWithImportOpen, setCampaignWithImportOpen] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [filtroEmpresa, setFiltroEmpresa] = useState('all');
  const [empresasParaFiltro, setEmpresasParaFiltro] = useState([]);
  // Estados para importación de contactos
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedCampaignForImport, setSelectedCampaignForImport] = useState(null);

  // Funciones de permisos basadas en roles
  const userRole = currentUser?.role || null;
  
  const hasPermission = (permission) => {
    console.log('🔒 Verificando permiso:', permission, 'para rol:', userRole);
    if (!userRole) {
      console.log('❌ No hay rol de usuario definido');
      return false;
    }
    const rolePermissions = {
      'admin': ['bulk-sender', 'dashboard', 'contacts', 'campaigns', 'analytics', 'settings', 'campaigns-delete'],
      'ejecutivo': ['bulk-sender', 'campaigns', 'settings'],
      'user': ['bulk-sender', 'campaigns'] // Permisos básicos para usuarios normales
    };
    // Verificar si el rol existe en el mapa de permisos
    if (!rolePermissions[userRole]) {
      console.log('❌ Rol no reconocido en el mapa de permisos:', userRole);
      return false;
    }
    const hasAccess = rolePermissions[userRole].includes(permission);
    console.log('✅ Tiene permiso?:', hasAccess);
    return hasAccess;
  };

  // Merge helper: cuando recibimos la lista desde el servidor, preserva el estado 'paused'
  // que el usuario haya establecido localmente para evitar sobrescribirlo por actualizaciones
  const mergeCampaignsPreservePaused = (existing, incoming) => {
    if (!Array.isArray(incoming)) return existing || [];
    const existingMap = new Map((existing || []).map(c => [c.id, c]));
    return incoming.map(c => {
      const ex = existingMap.get(c.id);
      if (ex && ex.status === 'paused' && c.status !== 'paused') {
        // Preservar paused y algunos campos de progreso locales si existen
        return {
          ...c,
          status: 'paused',
          messages_sent: (ex.messages_sent ?? c.messages_sent),
          messages_failed: (ex.messages_failed ?? c.messages_failed)
        };
      }
      return c;
    });
  };

  const canDeleteCampaign = () => {
    return hasPermission('campaigns-delete');
  };

  const getAllowedTabs = () => {
    if (!userRole) return [];
    const roleTabs = {
      'admin': [
          { key: 'bulk-sender', icon: Zap, label: 'Envío Masivo', count: 'NUEVO' },
          { key: 'dashboard', icon: BarChart3, label: 'Dashboard' },
          { key: 'contacts', icon: Users, label: 'Usuarios' },
          { key: 'campaigns', icon: Send, label: 'Campañas' },
          { key: 'analytics', icon: TrendingUp, label: 'Analíticas' },
          { key: 'settings', icon: Settings, label: 'Configuración' }
        ],
      'ejecutivo': [
        { key: 'bulk-sender', icon: Zap, label: 'Envío Masivo', count: 'NUEVO' },
        { key: 'campaigns', icon: Send, label: 'Campañas' },
        { key: 'settings', icon: Settings, label: 'Configuración' }
      ],
      'user': [
        { key: 'bulk-sender', icon: Zap, label: 'Envío Masivo', count: 'NUEVO' },
        { key: 'campaigns', icon: Send, label: 'Campañas' }
      ]
    };
    return roleTabs[userRole] || [];
  };

  // 🆕 FUNCIONES PARA MANEJO DE IMPORTACIÓN DE CAMPAÑAS
  const handleImportClick = (campaign) => {
    if (campaignWithImportOpen === campaign.id) {
      setCampaignWithImportOpen(null);
    } else {
      setCampaignWithImportOpen(campaign.id);
    }
  };

  const handleImportSuccess = (result) => {
    console.log('✅ Importación exitosa, actualizando campaña:', result);
    setCampaignsList(prev => 
      prev.map(campaign => 
        campaign.id === campaignWithImportOpen 
          ? { ...campaign, total_recipients: (campaign.total_recipients || 0) + (result.total_imported || result.successful_imports || 0) }
          : campaign
      )
    );
  };

  // ✅ INICIALIZAR SESIÓN AL CARGAR LA APLICACIÓN
  useEffect(() => {
    const initializeSession = async () => {
      try {
        console.log('🔐 Inicializando sesión...');
        // await initTestSession(); // Comentado temporalmente
        console.log('✅ Sesión inicializada correctamente');
      } catch (error) {
        console.error('❌ Error inicializando sesión:', error);
      }
    };

    initializeSession();
  }, []);

  // Cargar datos del dashboard
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await dashboardAPI.getStats();
        setStats(response.data.stats);
        setRecentCampaigns(response.data.recent_campaigns);
      } catch (error) {
        console.error('Error cargando dashboard:', error);
        // ✅ Usar datos de fallback si falla
        setStats({
          total_contacts: 12,
          messages_sent: 5,
          delivery_rate: 100,
          active_campaigns: 1
        });
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'dashboard') {
      fetchDashboardData();
    }
  }, [activeTab]);

  // Función para manejar el login exitoso
  const handleLogin = (userData) => {
    console.log('📝 Login exitoso - datos recibidos:', userData);
    
    // Determinar el rol basado en el username si role está vacío
    let normalizedRole;
    if (!userData.role || userData.role.trim() === '') {
      // Si el username es 'ejecutivo', asignar rol 'ejecutivo'
      normalizedRole = userData.username === 'ejecutivo' ? 'ejecutivo' : 'user';
      console.log('⚠️ Role vacío, asignando basado en username:', normalizedRole);
    } else {
      normalizedRole = userData.role.toString().toLowerCase();
      if (normalizedRole.includes('ejecutivo')) {
        normalizedRole = 'ejecutivo';
      }
    }
    
    console.log('🔑 Role normalizado:', normalizedRole);
    const normalizedUser = { ...userData, role: normalizedRole };
    setCurrentUser(normalizedUser);
    setIsAuthenticated(true);
    setIsInitialized(false); // Reset initialization flag
    // No establecer pestaña por defecto para mostrar el panel de bienvenida
    setActiveTab(''); 
  };

  // Función para cargar datos después del login
  const loadDataAfterLogin = async () => {
    if (!currentUser) return;

    try {
      console.log('🔄 Cargando campañas después del login...');
      const campaignsResponse = await campaignsAPI.list({ userId: currentUser.id });
      if (campaignsResponse.data && campaignsResponse.data.data && campaignsResponse.data.data.campaigns) {
        setCampaignsList(prev => mergeCampaignsPreservePaused(prev, campaignsResponse.data.data.campaigns || []));
      } else {
        setCampaignsList(prev => mergeCampaignsPreservePaused(prev, []));
      }

      // Cargar empresas
      const empresasResponse = await fetch('/sistemasms/backend/api/settings/get-empresas.php', {
        credentials: 'include'
      });
      const empresasData = await empresasResponse.json();
      if (empresasData.success) {
        setEmpresasParaFiltro(empresasData.empresas || []);
      }
    } catch (error) {
      console.error('❌ Error cargando datos después del login:', error);
    }
  };

  // Función para logout
  const handleLogout = () => {
    setCurrentUser(null);
    setIsAuthenticated(false);
    setIsInitialized(false);
    setActiveTab('bulk-sender');
    setCampaignsList([]);
    setEmpresasParaFiltro([]);
  };

  // useEffect para cargar datos después del login (solo una vez)
  useEffect(() => {
    if (currentUser && !isInitialized) {
      loadDataAfterLogin();
      setIsInitialized(true);
    }
  }, [currentUser, isInitialized]);

  // useEffect para redirigir a pestaña válida si la actual no es permitida
  useEffect(() => {
    if (currentUser && isInitialized) {
      const allowedTabs = getAllowedTabs();
      const currentTabAllowed = allowedTabs.some(tab => tab.key === activeTab);

      if (!currentTabAllowed && allowedTabs.length > 0) {
        setActiveTab(allowedTabs[0].key);
      }
    }
  }, [activeTab, userRole, isInitialized, currentUser]); // Usar userRole en lugar de currentUser?.role

  // Cargar campañas reales desde la API
  useEffect(() => {
    if (activeTab === 'campaigns' && currentUser && isInitialized) {
      const fetchCampaigns = async () => {
        try {
          console.log('🔄 Cargando campañas desde la API...');
          const response = await campaignsAPI.list({ userId: currentUser.id });
          if (response.data && response.data.data && response.data.data.campaigns) {
            console.log(`✅ ${response.data.data.campaigns.length} campañas cargadas correctamente`);
            setCampaignsList(response.data.data.campaigns);
          } else {
            setCampaignsList([]);
          }
        } catch (error) {
          console.error('❌ Error cargando campañas:', error);
          setCampaignsList([]);
        }
      };

      fetchCampaigns();
    }
  }, [activeTab, currentUser?.id, isInitialized]); // Usar currentUser?.id para ser más específico

  // Hook personalizado para polling de progreso (actualización automática cada 4s)
  // Solo activar cuando esté inicializado y en la pestaña de campañas
  useCampaignProgress(
    activeTab === 'campaigns' && isInitialized ? activeTab : null,
    importingFile,
    setCampaignsList
  );

  // Cargar empresas cuando se abre el formulario
  useEffect(() => {
    const fetchEmpresas = async () => {
      if (showCreateForm) {
        try {
          const response = await fetch('/sistemasms/backend/api/settings/get-empresas.php', {
            credentials: 'include'
          });
          const data = await response.json();
          if (data.success) {
            setEmpresas(data.data.empresas || []);
          }
        } catch (error) {
          console.error('Error cargando empresas:', error);
          // Datos de fallback
          setEmpresas([
            { id: 1, empresa_name: 'Claro' },
            { id: 2, empresa_name: 'Entel' },
            { id: 3, empresa_name: 'Movistar' },
            { id: 4, empresa_name: 'WOM' }
          ]);
        }
      }
    };
    fetchEmpresas();
  }, [showCreateForm]);

  // 2. AGREGAR ESTE useEffect PARA CARGAR EMPRESAS (después de línea ~1900)
  useEffect(() => {
    const fetchEmpresasParaFiltro = async () => {
      if (activeTab === 'campaigns') {
        try {
          console.log('🏢 Cargando empresas para filtro...');
          const response = await fetch('/sistemasms/backend/api/settings/get-empresas.php', {
            credentials: 'include'
          });
          const empresasData = await response.json();
          if (empresasData.success) {
            setEmpresasParaFiltro(empresasData.empresas || []); // ✅ CORREGIDO: quitar .data
            console.log('✅ Empresas cargadas para filtro:', empresasData.empresas);
          }
        } catch (error) {
          console.error('Error cargando empresas para filtro:', error);
          setEmpresasParaFiltro([]);
        }
      }
    };
    fetchEmpresasParaFiltro();
  }, [activeTab]);

  // Cargar contactos cuando se selecciona la pestaña
  useEffect(() => {
    const fetchContacts = async () => {
      if (activeTab === 'contacts') {
        try {
          // Cargamos ejecutivos en lugar de contactos tradicionales
          try {
            const response = await executivesAPI.list();
            // Soportar varias formas de respuesta
            const raw = response.data?.executives || response.data?.data || response.data || [];
            // Normalizar ids y campos esperados (id numérico, enabled boolean)
            const list = Array.isArray(raw) ? raw : (raw.executives || []);
            const normalized = list.map(item => {
              const id = item.id ?? item.ID ?? item.user_id ?? null;
              return {
                ...item,
                id: id !== null && id !== undefined ? Number(id) : null,
                enabled: item.enabled === undefined ? (item.is_active === 1 || item.is_active === true) : !!item.enabled
              };
            }).filter(i => i.id !== null);
            console.log('🔁 Ejecutivos cargados (normalized):', normalized.length, normalized);
            setExecutives(normalized);
          } catch (err) {
            console.error('Error cargando ejecutivos:', err);
            setExecutives([]);
          }
        } catch (error) {
          console.error('Error cargando contactos:', error);
          // ✅ Datos de fallback
          setExecutives([
            { id: 1, username: 'ejecutivo1', full_name: 'Juan Pérez', email: 'juan@email.com', enabled: true }
          ]);
        }
      }
    };

    fetchContacts();
  }, [activeTab]);

  // Cargar analytics - ✅ CORREGIDO
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (activeTab === 'analytics') {
        try {
          setAnalyticsLoading(true);
          console.log('📊 Cargando analytics...');
          const response = await analyticsAPI.getAnalytics(analyticsPeriod);
          console.log('📊 Analytics response:', response);
          setAnalyticsData(response.data);
        } catch (error) {
          console.error('Error cargando analytics:', error);
          // ✅ Usar datos de fallback en caso de error
          setAnalyticsData({
            period_stats: {
              total_campaigns: 5,
              total_recipients: 250,
              total_messages_sent: 240,
              avg_delivery_rate: 96.0
            },
            messages_by_day: [
              { date: '2025-06-17', messages_sent: 50, delivered: 48, failed: 2 },
              { date: '2025-06-16', messages_sent: 30, delivered: 30, failed: 0 }
            ],
            campaigns_by_status: [
              { status: 'completed', count: 3 },
              { status: 'sending', count: 1 },
              { status: 'draft', count: 1 }
            ],
            top_campaigns: [
              { campaign_name: 'Promoción Navidad', total_recipients: 1250, delivered_count: 1200, delivery_rate: 96.0 }
            ],
            contacts_by_group: [
              { group_name: 'Clientes VIP', contact_count: 8 },
              { group_name: 'Sin Grupo', contact_count: 4 }
            ],
            hourly_activity: Array.from({ length: 24 }, (_, hour) => ({
              hour,
              messages_count: Math.floor(Math.random() * 20)
            }))
          });
        } finally {
          setAnalyticsLoading(false);
        }
      }
    };

    fetchAnalytics();
  }, [activeTab, analyticsPeriod]);

  // Cargar settings - ✅ CORREGIDO
  useEffect(() => {
    const fetchSettings = async () => {
      if (activeTab === 'settings') {
        try {
          setSettingsLoading(true);
          console.log('⚙️ Cargando settings...');
          const response = await settingsAPI.getSettings();
          console.log('⚙️ Settings response:', response);
          setSettingsData(response.data);
          setProfileForm(response.data.user_data);
        } catch (error) {
          console.error('Error cargando configuración:', error);
          // ✅ Usar datos de fallback en caso de error
          setSettingsData({
            user_data: {
              username: 'admin',
              email: 'admin@messagehub.com',
              full_name: 'Administrador del Sistema',
              created_at: '2025-01-01T00:00:00Z'
            },
            user_stats: {
              total_contacts: 12,
              total_campaigns: 31,
              total_messages: 29,
              total_groups: 7
            },
            contact_groups: [
              { id: 1, group_name: 'Clientes VIP', total_contacts: 8, created_at: '2025-01-01' },
              { id: 2, group_name: 'Prospectos', total_contacts: 2, created_at: '2025-01-15' }
            ],
            settings: {
              notifications_enabled: { value: '1', description: 'Activar notificaciones' },
              auto_backup: { value: '1', description: 'Respaldo automático' },
              dark_mode: { value: '0', description: 'Modo oscuro' }
            }
          });
          setProfileForm({
            username: 'admin',
            email: 'admin@messagehub.com',
            full_name: 'Administrador del Sistema'
          });
        } finally {
          setSettingsLoading(false);
        }
      }
    };

    fetchSettings();
  }, [activeTab]);

  // 🆕 Cargar datos del Dashboard con filtros
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (activeTab === 'dashboard') {
        try {
          setDashboardLoading(true);
          console.log('📊 Cargando datos del dashboard con filtros:', dashboardFilters);
          
          const params = new URLSearchParams({
            empresa_id: dashboardFilters.empresa_id,
            period: dashboardFilters.period,
            ...(dashboardFilters.start_date && { start_date: dashboardFilters.start_date }),
            ...(dashboardFilters.end_date && { end_date: dashboardFilters.end_date })
          });
          
          const response = await fetch(`/sistemasms/backend/api/dashboard/campaigns-stats.php?${params}`, {
            credentials: 'include'
          });
          
          const result = await response.json();
          console.log('📊 Dashboard data:', result);
          
          if (result.success) {
            setDashboardData(result.data);
          } else {
            console.error('Error en respuesta del dashboard:', result.message);
            setDashboardData(null);
          }
        } catch (error) {
          console.error('Error cargando datos del dashboard:', error);
          setDashboardData(null);
        } finally {
          setDashboardLoading(false);
        }
      }
    };

    fetchDashboardData();
  }, [activeTab, dashboardFilters]);

  // 🆕 Cargar datos de KPI Cancelaciones
  useEffect(() => {
    const fetchKpiData = async () => {
      if (activeTab === 'dashboard') {
        try {
          setKpiLoading(true);
          const params = new URLSearchParams({ 
            period: kpiPeriod,
            empresa_id: kpiEmpresaId
          });
          
          const response = await fetch(`/sistemasms/backend/api/kpi/get-cancelaciones-stats.php?${params}`, {
            credentials: 'include'
          });
          
          const result = await response.json();
          
          if (result.success) {
            setKpiData(result.data);
          } else {
            setKpiData(null);
          }
        } catch (error) {
          console.error('Error cargando KPI:', error);
          setKpiData(null);
        } finally {
          setKpiLoading(false);
        }
      }
    };

    fetchKpiData();
  }, [activeTab, kpiPeriod, kpiEmpresaId]);

  const MenuItem = ({ icon: Icon, label, tabKey, count }) => {
    const isActive = activeTab === tabKey;
    return (
      <button
        onClick={() => setActiveTab(tabKey)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.7rem 1rem',
          textAlign: 'left',
          borderRadius: 'var(--radius-md)',
          transition: 'all 0.2s ease',
          fontWeight: 500,
          fontSize: '0.9rem',
          background: isActive ? 'var(--accent-gradient)' : 'transparent',
          color: isActive ? '#fff' : 'var(--text-tertiary)',
          boxShadow: isActive ? 'var(--shadow-md)' : 'none',
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'var(--sidebar-item-hover)';
            e.currentTarget.style.color = 'var(--text-primary)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--text-tertiary)';
          }
        }}
      >
        <Icon size={19} />
        <span>{label}</span>
        {count !== undefined && count > 0 && (
          <span style={{
            marginLeft: 'auto',
            backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'var(--info-bg)',
            color: isActive ? '#fff' : 'var(--accent-primary)',
            padding: '0.15rem 0.5rem',
            borderRadius: '9999px',
            fontSize: '0.7rem',
            fontWeight: 700
          }}>
            {count}
          </span>
        )}
      </button>
    );
  };


  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue" }) => (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${
          color === 'blue' ? 'bg-blue-100' :
          color === 'green' ? 'bg-green-100' :
          color === 'purple' ? 'bg-purple-100' :
          'bg-orange-100'
        }`}>
          <Icon className={
            color === 'blue' ? 'text-blue-600' :
            color === 'green' ? 'text-green-600' :
            color === 'purple' ? 'text-purple-600' :
            'text-orange-600'
          } size={24} />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-800">{loading ? '...' : value}</h3>
          <p className="text-gray-600 font-medium">{title}</p>
          {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

    const handleCreateCampaign = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!campaignForm.empresa_id) {
      alert('Por favor selecciona una empresa');
      return;
    }
    
    if (!campaignForm.campaign_name || !campaignForm.message_content) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      // Preparar datos de la campaña
      const campaignData = {
        ...campaignForm,
        empresa_id: parseInt(campaignForm.empresa_id)
      };

      const response = await campaignsAPI.create(campaignData);
      
      // ✅ CORREGIR EL MANEJO DE LA RESPUESTA
      if (response && response.data && response.data.success) {
        // Recargar lista de campañas
  const campaignsResponse = await campaignsAPI.list({ userId: 1 });
  setCampaignsList(prev => mergeCampaignsPreservePaused(prev, campaignsResponse.data.data.campaigns || []));
        
        // Limpiar formulario
        setCampaignForm({
          campaign_name: '',
          message_content: '',
          empresa_id: '',
          is_scheduled: false,
          send_delay_seconds: 5,
          scheduled_at: ''
        });
        
        alert(`✅ Campaña creada exitosamente! ${response.data.total_recipients} destinatarios`);
        setShowCreateForm(false);
      } else {
        alert('Error: ' + (response?.data?.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error creando campaña:', error);
      alert('Error creando campaña: ' + error.message);
    }
  };


  // REEMPLAZA ESTO (líneas 1885-1901):
  const handleSendCampaign = async (campaignId) => {
    if (window.confirm('¿Estás seguro de enviar esta campaña? Esta acción no se puede deshacer.')) {
      try {
        alert('Iniciando envío de la campaña...');

        const response = await fetch('/sistemasms/backend/api/campaigns/send.php', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ campaign_id: campaignId })
        });

        if (!response.ok) {
          if (response.status === 409) {
            alert('La campaña está pausada y no se puede iniciar.');
            return;
          }
          throw new Error('Error HTTP: ' + response.status);
        }

        const result = await response.json();
        if (result.success || result.message) {
          // Actualizar estado solo tras confirmación
          setCampaignsList(prev => prev.map(c => c.id === campaignId ? { ...c, status: 'sending' } : c));
          alert('Campaña iniciada: ' + (result.message || 'Envío en progreso'));
        }
      } catch (error) {
        alert('Error al enviar campaña: ' + error.message);
      }
    }
  };

  // REEMPLAZA la función sendCampaignImmediately en tu App.js

  const sendCampaignImmediately = async (campaignId, campaignName) => {
    const confirmed = window.confirm(`¿Estás seguro de que quieres enviar la campaña "${campaignName}" AHORA via Meta API?\n\nEsto enviará mensajes de WhatsApp a todos los contactos asociados usando la API oficial de Meta.`);
    
    if (!confirmed) return;

    try {
      console.log('🚀 Enviando campaña via Meta API:', campaignId);

      const response = await fetch('/sistemasms/backend/api/whatsapp/send-campaign.php', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          campaign_id: campaignId
        })
      });

      const result = await response.json();
      console.log('📊 Respuesta del servidor Meta API:', result);

      if (result.success) {
        alert(`✅ Campaña "${campaignName}" enviada via Meta API!\n\n` +
              `📊 Resultados:\n` +
              `• Total contactos: ${result.total_contacts}\n` +
              `• Mensajes enviados: ${result.messages_sent}\n` +
              `• Mensajes fallidos: ${result.messages_failed}\n` +
              `• Tasa de éxito: ${result.success_rate}%\n`);
        
        setCampaignsList(prev => 
          prev.map(campaign => 
            campaign.id === campaignId 
              ? ({ ...campaign, status: 'completed', messages_sent: result.messages_sent || 0 })
              : campaign
          )
        );
      } else {
        throw new Error(result.message || 'Error desconocido en el servidor');
      }
        
    } catch (error) {
      console.error('❌ Error sending campaign:', error);
      alert(`❌ Error enviando campaña "${campaignName}":\n\n${error.message}\n\nRevisa la consola para más detalles.`);
    }
  };

  const recargarEmpresasCompleto = async () => {
    try {
      console.log('🔄 Recargando empresas...');
      
      const response = await fetch('/sistemasms/backend/api/settings/get-empresas.php', {
        credentials: 'include',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Empresas recibidas:', data);
        
        if (data && data.success && data.empresas) {
          setEmpresasParaFiltro(data.empresas);
          console.log('✅ Estado actualizado con empresas:', data.empresas);
          return true;
        } else {
          console.warn('⚠️ Respuesta inesperada:', data);
          setEmpresasParaFiltro([]);
          return false;
        }
      } else {
        console.error('❌ Error HTTP:', response.status);
        setEmpresasParaFiltro([]);
        return false;
      }
    } catch (error) {
      console.error('❌ Error recargando empresas:', error);
      setEmpresasParaFiltro([]);
      return false;
    }
  };
  
 // Función simple para crear empresa (CORREGIDA para tu PHP)
  const crearEmpresaSimple = async () => {
    const nombreEmpresa = prompt('📝 Nombre de la nueva empresa:');
    if (!nombreEmpresa || !nombreEmpresa.trim()) return;
    
    const descripcion = prompt('📄 Descripción (opcional):') || '';
    
    try {
      console.log('🏢 Creando empresa:', nombreEmpresa);
      
      const response = await fetch('/sistemasms/backend/api/settings/create-empresa.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          empresa_name: nombreEmpresa.trim(),
          description: descripcion.trim()
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('📊 Respuesta crear empresa:', result);
      
      if (result.success) {
        alert(`✅ Empresa "${nombreEmpresa}" creada exitosamente!`);
        
        // ✅ CORREGIDO: Tu PHP devuelve data.empresas (no data.data.empresas)
        try {
          const empresasResponse = await fetch('/sistemasms/backend/api/settings/get-empresas.php', {
            credentials: 'include'
          });
          
          if (empresasResponse.ok) {
            const empresasData = await empresasResponse.json();
            console.log('📊 Respuesta get-empresas:', empresasData);
            
            // ✅ ESTRUCTURA CORREGIDA: empresasData.empresas (no empresasData.data.empresas)
            if (empresasData && empresasData.success && empresasData.empresas) {
              setEmpresasParaFiltro(empresasData.empresas);
              console.log('✅ Lista de empresas actualizada');
            } else {
              console.warn('⚠️ Estructura inesperada:', empresasData);
              window.location.reload();
            }
          } else {
            console.error('❌ Error cargando empresas:', empresasResponse.status);
            window.location.reload();
          }
        } catch (reloadError) {
          console.error('❌ Error recargando empresas:', reloadError);
          alert('✅ Empresa creada, pero hubo un error recargando la lista. La página se actualizará.');
          window.location.reload();
        }
      } else {
        alert('❌ Error: ' + result.message);
      }
    } catch (error) {
      console.error('❌ Error creando empresa:', error);
      alert('❌ Error creando empresa: ' + error.message);
    }
  };

  // Función para eliminar empresa 
  const eliminarEmpresaSimple = async (empresaId, empresaName) => {
    console.log('🗑️ Iniciando eliminación de empresa:', empresaId, empresaName);
    
    const campanasAsociadas = campaignsList.filter(c => c.empresa_id === empresaId);
    
    const mensaje = `⚠️ ELIMINAR EMPRESA\n\n` +
                    `🏢 Empresa: ${empresaName}\n` +
                    `📊 Campañas asociadas: ${campanasAsociadas.length}\n\n` +
                    `${campanasAsociadas.length > 0 
                      ? `Se eliminarán también las campañas:\n${campanasAsociadas.map(c => `• ${c.campaign_name}`).join('\n')}\n\n`
                      : ''
                    }` +
                    `Esta acción NO se puede deshacer.\n\n¿Continuar?`;
    
    if (!window.confirm(mensaje)) return;
    
    if (campanasAsociadas.length > 0) {
      const confirmar = prompt(`Para confirmar, escribe "${empresaName}":`);
      if (confirmar !== empresaName) {
        alert('❌ Nombre incorrecto. Eliminación cancelada.');
        return;
      }
    }
    
    try {
      console.log('🚀 Enviando petición de eliminación...');
      
      const response = await fetch(`/sistemasms/backend/api/settings/delete-empresa.php?empresa_id=${empresaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const result = await response.json();
      console.log('📊 Resultado eliminación:', result);
      
      if (result.success) {
        alert(`✅ Empresa "${empresaName}" eliminada exitosamente\n${campanasAsociadas.length > 0 ? `📊 ${campanasAsociadas.length} campañas también fueron eliminadas` : ''}`);
        
        console.log('🔄 Recargando datos después de eliminación...');
        
        // 1. Recargar campañas
        try {
          const campaignsResponse = await campaignsAPI.list({ userId: 1 });
          setCampaignsList(prev => mergeCampaignsPreservePaused(prev, campaignsResponse.data.data.campaigns || []));
          console.log('✅ Campañas recargadas');
        } catch (campaignsError) {
          console.error('❌ Error recargando campañas:', campaignsError);
        }
        
        // 2. ✅ USAR LA NUEVA FUNCIÓN DE RECARGA
        const empresasRecargadas = await recargarEmpresasCompleto();
        
        if (empresasRecargadas) {
          console.log('✅ Empresas recargadas exitosamente');
        } else {
          console.warn('⚠️ Error recargando empresas, recargando página...');
          alert('✅ Empresa eliminada. La página se actualizará para reflejar los cambios.');
          window.location.reload();
        }
        
        // 3. Reset filtro si era la empresa eliminada
        if (filtroEmpresa == empresaId) {
          setFiltroEmpresa('all');
          console.log('🔄 Filtro reseteado a "all"');
        }
        
        console.log('✅ Eliminación completada exitosamente');
        
      } else {
        // ✅ MANEJO ESPECÍFICO PARA "EMPRESA NO ENCONTRADA"
        if (result.message.includes('no encontrada')) {
          alert(`ℹ️ La empresa "${empresaName}" ya fue eliminada anteriormente.\n\nActualizando la lista...`);
          
          // Recargar datos para sincronizar
          await recargarEmpresasCompleto();
          
          try {
            const campaignsResponse = await campaignsAPI.list({ userId: 1 });
            setCampaignsList(prev => mergeCampaignsPreservePaused(prev, campaignsResponse.data.data.campaigns || []));
          } catch (error) {
            console.error('Error recargando campañas:', error);
          }
          
          if (filtroEmpresa == empresaId) {
            setFiltroEmpresa('all');
          }
        } else {
          alert('❌ Error: ' + result.message);
        }
      }
    } catch (error) {
      console.error('❌ Error eliminando empresa:', error);
      alert('❌ Error eliminando empresa: ' + error.message);
    }
  };

    // AGREGAR ESTA FUNCIÓN cerca de la línea 1900, después de sendCampaignImmediately
  const exportDetailedReport = async (campaignId, campaignName) => {
    try {
      const response = await fetch(`/sistemasms/backend/api/campaigns/export-detailed-report.php?campaign_id=${campaignId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reporte_Detallado_${campaignName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      alert('✅ Reporte detallado descargado exitosamente!');
    } catch (error) {
      alert('❌ Error: ' + error.message);
    }
  };

  // 3. FUNCIÓN PARA FILTRAR CAMPAÑAS (agregar cerca de línea 2000)
  const filtrarCampañasPorEmpresa = (campañas) => {
    if (filtroEmpresa === 'all') {
      return campañas;
    }
    
    if (filtroEmpresa === 'sin-empresa') {
      return campañas.filter(campaign => !campaign.empresa_id);
    }
    
    return campañas.filter(campaign => campaign.empresa_id === parseInt(filtroEmpresa));
  };

  // 4. FUNCIÓN PARA OBTENER NOMBRE DE EMPRESA (agregar cerca de línea 2010)
  const obtenerNombreEmpresa = (empresaId) => {
    if (!empresaId) return 'Sin Empresa';
    const empresa = empresasParaFiltro.find(emp => emp.id === empresaId);
    return empresa ? empresa.empresa_name : `Empresa ID: ${empresaId}`;
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await settingsAPI.updateSettings({ user_data: profileForm });
      setEditingProfile(false);
      alert('Perfil actualizado exitosamente');
    } catch (error) {
      alert('Error actualizando perfil: ' + error.message);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await settingsAPI.updateSettings({ new_group: newGroupForm });
      setNewGroupForm({ group_name: '', description: '' });
      setShowNewGroupForm(false);
      // Recargar datos
      const response = await settingsAPI.getSettings();
      setSettingsData(response.data);
      alert('Grupo creado exitosamente');
    } catch (error) {
      alert('Error creando grupo: ' + error.message);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (window.confirm('¿Estás seguro de eliminar este grupo? Los contactos se moverán a "Sin Grupo".')) {
      try {
        await settingsAPI.deleteGroup(groupId);
        // Recargar datos
        const response = await settingsAPI.getSettings();
        setSettingsData(response.data);
        alert('Grupo eliminado exitosamente');
      } catch (error) {
        alert('Error eliminando grupo: ' + error.message);
      }
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert('Las contraseñas no coinciden');
      return;
    }

    try {
      await settingsAPI.updateSettings({ change_password: passwordForm });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordForm(false);
      alert('Contraseña cambiada exitosamente');
    } catch (error) {
      alert('Error cambiando contraseña: ' + error.message);
    }
  };

  const renderDashboard = () => {
    if (dashboardLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando estadísticas...</p>
          </div>
        </div>
      );
    }

    return (
    <div className="space-y-6">
        {/* ENCABEZADO CON FILTROS */}
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Dashboard de Campañas</h1>
        <div className="flex gap-3">
          <button 
            onClick={() => setActiveTab('campaigns')}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Send size={18} />
            Nueva Campaña
          </button>
        </div>
      </div>

          {/* FILTROS */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Filtro de Empresa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter size={16} className="inline mr-2" />
                Empresa
              </label>
              <select
                value={dashboardFilters.empresa_id}
                onChange={(e) => setDashboardFilters({ ...dashboardFilters, empresa_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todas las empresas</option>
                {dashboardData?.empresas?.map(empresa => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.empresa_name} ({empresa.campaigns_count})
                  </option>
                ))}
                <option value="sin-empresa">Sin Empresa</option>
              </select>
            </div>

            {/* Filtro de Período */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} className="inline mr-2" />
                Período
              </label>
              <select
                value={dashboardFilters.period}
                onChange={(e) => setDashboardFilters({ ...dashboardFilters, period: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="day">Hoy</option>
                <option value="week">Última Semana</option>
                <option value="month">Último Mes</option>
                <option value="custom">Personalizado</option>
              </select>
            </div>

            {/* Fecha Inicio (solo si es personalizado) */}
            {dashboardFilters.period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={dashboardFilters.start_date}
                    onChange={(e) => setDashboardFilters({ ...dashboardFilters, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={dashboardFilters.end_date}
                    onChange={(e) => setDashboardFilters({ ...dashboardFilters, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}
          </div>
        </div>


        {/* GRÁFICO DE CAMPAÑAS POR DÍA */}
        {dashboardData?.data_by_date && dashboardData.data_by_date.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-gray-800 mb-6">Campañas y Mensajes por Día</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={dashboardData.data_by_date}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Legend />
                <Bar dataKey="enviados" fill="#8b5cf6" name="Enviados" />
                <Bar dataKey="entregados" fill="#10b981" name="Entregados" />
                <Bar dataKey="no_enviados" fill="#f59e0b" name="No Enviados" />
              </BarChart>
            </ResponsiveContainer>
      </div>
        )}

        {/* LISTA DE CAMPAÑAS DEL PERÍODO */}
        {dashboardData?.campaigns && dashboardData.campaigns.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-gray-800 mb-6">
              Campañas del Período ({dashboardData.campaigns.length})
            </h3>

          <div className="space-y-4">
              {dashboardData.campaigns.map((campaign) => (
                <div key={campaign.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                  <div className="flex items-start justify-between mb-3">
                  <div>
                      <h4 className="font-semibold text-gray-800 text-lg">{campaign.campaign_name}</h4>
                      <p className="text-sm text-gray-600">
                        {campaign.empresa_name || 'Sin Empresa'} • {new Date(campaign.created_at).toLocaleDateString('es-ES')}
                      </p>
                  </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      campaign.status === 'completed' ? 'bg-green-100 text-green-700' :
                      campaign.status === 'sending' ? 'bg-blue-100 text-blue-700' :
                      campaign.status === 'scheduled' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {campaign.status}
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">{campaign.stats.enviados}</div>
                      <div className="text-xs text-gray-600 mt-1">Enviados</div>
                </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{campaign.stats.entregados}</div>
                      <div className="text-xs text-gray-600 mt-1">Entregados</div>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">{campaign.stats.no_enviados}</div>
                      <div className="text-xs text-gray-600 mt-1">No Enviados</div>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{campaign.stats.fallidos}</div>
                      <div className="text-xs text-gray-600 mt-1">Fallidos</div>
          </div>
        </div>

                  {/* Barra de progreso */}
                  <div className="mt-4">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="flex h-3 rounded-full overflow-hidden">
                        <div
                          className="bg-green-500"
                          style={{
                            width: `${campaign.stats.total > 0 ? (campaign.stats.entregados / campaign.stats.total) * 100 : 0}%`
                          }}
                        ></div>
                        <div
                          className="bg-purple-500"
                          style={{
                            width: `${campaign.stats.total > 0 ? ((campaign.stats.enviados - campaign.stats.entregados) / campaign.stats.total) * 100 : 0}%`
                          }}
                        ></div>
                        <div
                          className="bg-orange-500"
                          style={{
                            width: `${campaign.stats.total > 0 ? (campaign.stats.no_enviados / campaign.stats.total) * 100 : 0}%`
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 mt-1 text-right">
                      {campaign.stats.total > 0 ?
                        `${Math.round((campaign.stats.entregados / campaign.stats.total) * 100)}% de entrega`
                        : 'Sin datos'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MENSAJE CUANDO NO HAY DATOS */}
        {dashboardData && (!dashboardData.campaigns || dashboardData.campaigns.length === 0) && (
          <div className="bg-white rounded-xl p-12 shadow-lg">
            <div className="text-center text-gray-500">
              <Send size={64} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay campañas en este período</h3>
              <p className="text-gray-600 mb-6">
                No se encontraron campañas para los filtros seleccionados.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setDashboardFilters({ empresa_id: 'all', period: 'month', start_date: '', end_date: '' })}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Restablecer Filtros
                </button>
            <button 
              onClick={() => setActiveTab('campaigns')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Crear Nueva Campaña
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🆕 SECCIÓN KPI DE CANCELACIONES */}
        <div className="bg-white rounded-xl p-6 shadow-lg mt-8 border-t-4 border-red-500">
          <div className="flex items-center justify-between mb-6">
              <div>
              <h2 className="text-2xl font-bold text-gray-800">KPI de Cancelaciones</h2>
              <p className="text-sm text-gray-600">Seguimiento de clientes que han cancelado sus servicios</p>
              </div>
            <div className="flex gap-3">
              <select
                value={kpiEmpresaId}
                onChange={(e) => setKpiEmpresaId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="all">Todas las empresas</option>
                {kpiData?.empresas?.map(empresa => (
                  <option key={empresa.id} value={empresa.id}>
                    {empresa.empresa_name} ({empresa.cancelaciones_count})
                  </option>
                ))}
                <option value="sin-empresa">Sin Empresa</option>
              </select>
              <select
                value={kpiPeriod}
                onChange={(e) => setKpiPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="day">Hoy</option>
                <option value="week">Última Semana</option>
                <option value="month">Último Mes</option>
              </select>
              {kpiData && kpiData.totals && kpiData.totals.total_cancelaciones > 0 && (
                <button
                  onClick={async () => {
                    if (window.confirm('¿Estás seguro de eliminar TODOS los datos de KPI? Esta acción no se puede deshacer.')) {
                      try {
                        const response = await fetch('/sistemasms/backend/api/kpi/delete-all.php', {
                          method: 'DELETE',
                          credentials: 'include'
                        });
                        const result = await response.json();
                        if (result.success) {
                          alert(`✅ ${result.message}`);
                          // Recargar datos
                          const params = new URLSearchParams({ period: kpiPeriod });
                          const kpiResponse = await fetch(`/sistemasms/backend/api/kpi/get-cancelaciones-stats.php?${params}`, {
                            credentials: 'include'
                          });
                          const kpiResult = await kpiResponse.json();
                          if (kpiResult.success) {
                            setKpiData(kpiResult.data);
                          } else {
                            setKpiData(null);
                          }
                        }
                      } catch (error) {
                        alert('Error: ' + error.message);
                      }
                    }
                  }}
                  className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                  title="Eliminar todos los datos de KPI"
                >
                  <Trash2 size={18} />
                  Limpiar Datos
                </button>
              )}
              <button
                onClick={() => setShowKpiImport(true)}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                <Plus size={18} />
                Importar Excel
              </button>
            </div>
          </div>

          {kpiLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
          ) : kpiData ? (
            <>
              {/* Métricas KPI */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                  <div className="flex items-center justify-between mb-2">
                    <Users className="text-red-600" size={24} />
                    <div className="text-3xl font-bold text-red-600">{kpiData.totals.total_cancelaciones}</div>
                  </div>
                  <div className="text-red-800 font-medium">Total Cancelaciones</div>
                </div>

                <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="text-orange-600" size={24} />
                    <div className="text-3xl font-bold text-orange-600">
                      ${kpiData.totals.monto_total_perdido?.toLocaleString() || 0}
                    </div>
                  </div>
                  <div className="text-orange-800 font-medium">Monto Total Perdido</div>
                </div>

                <div className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                  <div className="flex items-center justify-between mb-2">
                    <Target className="text-yellow-600" size={24} />
                    <div className="text-3xl font-bold text-yellow-600">
                      ${kpiData.totals.monto_promedio?.toLocaleString() || 0}
                    </div>
                  </div>
                  <div className="text-yellow-800 font-medium">Monto Promedio</div>
                </div>
              </div>

              {/* Gráfico de cancelaciones por día */}
              {kpiData.by_day && kpiData.by_day.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Cancelaciones por Día</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={kpiData.by_day}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="fecha" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="cantidad" stroke="#dc2626" name="Cancelaciones" strokeWidth={2} />
                      <Line type="monotone" dataKey="monto_total" stroke="#f59e0b" name="Monto ($)" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Cancelaciones recientes */}
              {kpiData.recientes && kpiData.recientes.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Cancelaciones Recientes</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">RUT</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Cliente</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Empresa</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Monto</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Fecha</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kpiData.recientes.map((item) => (
                          <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 font-mono text-xs">{item.rut}</td>
                            <td className="py-3 px-4">{item.nombre_cliente || '-'}</td>
                            <td className="py-3 px-4 text-xs">
                              {item.empresa_name ? (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">{item.empresa_name}</span>
                              ) : (
                                <span className="text-gray-400">Sin empresa</span>
                              )}
                            </td>
                            <td className="py-3 px-4 font-semibold text-red-600">${item.monto_pagado.toLocaleString()}</td>
                            <td className="py-3 px-4">{new Date(item.fecha_cancelacion).toLocaleDateString('es-ES')}</td>
                            <td className="py-3 px-4 text-gray-600 text-xs">{item.motivo_cancelacion || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Mensaje si no hay datos */}
              {(!kpiData.recientes || kpiData.recientes.length === 0) && (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">No hay cancelaciones registradas</p>
                  <p className="text-sm mt-2">Importa un archivo Excel para comenzar</p>
        </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No hay datos de KPI disponibles</p>
              <button
                onClick={() => setShowKpiImport(true)}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Importar Primer Excel
              </button>
            </div>
          )}
      </div>
    </div>
  );
  };

  // 🆕 Función para importar Excel de cancelaciones
  const handleKpiImport = async (file, empresaId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (empresaId && empresaId !== 'all' && empresaId !== 'sin-empresa') {
        formData.append('empresa_id', empresaId);
      }

      const response = await fetch('/sistemasms/backend/api/kpi/import-cancelaciones.php', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Mostrar info de debug en consola
        console.log('📊 Debug Info de Importación:', result.data.debug_info);
        
        let message = `✅ Importación exitosa!\n\n` +
              `• Total filas: ${result.data.total_rows}\n` +
              `• Importadas: ${result.data.imported}\n` +
              `• Errores: ${result.data.errors}`;
        
        // Mostrar primeras filas procesadas para debug
        if (result.data.debug_info && result.data.debug_info.length > 0) {
          message += '\n\n📊 DEBUG - Primeras filas procesadas:';
          result.data.debug_info.slice(0, 3).forEach(row => {
            message += `\n\nFila ${row.fila}:`;
            message += `\n  • RUT: ${row.rut}`;
            message += `\n  • Monto RAW: ${row.monto_raw} (${row.monto_raw_tipo})`;
            message += `\n  • Monto Procesado: ${row.monto_procesado}`;
            message += `\n  • Fecha Procesada: ${row.fecha_procesada}`;
          });
        }
        
        alert(message);
        
        // Recargar datos
        const params = new URLSearchParams({ period: kpiPeriod });
        const kpiResponse = await fetch(`/sistemasms/backend/api/kpi/get-cancelaciones-stats.php?${params}`, {
          credentials: 'include'
        });
        const kpiResult = await kpiResponse.json();
        if (kpiResult.success) {
          setKpiData(kpiResult.data);
        }
        setShowKpiImport(false);
      } else {
        alert('❌ Error: ' + result.message);
      }
    } catch (error) {
      console.error('Error importando KPI:', error);
      alert('❌ Error al importar: ' + error.message);
    }
  };

  const renderContacts = () => (
    <ExecutiveUsersManager executives={executives} setExecutives={setExecutives} />
  );

// ==========================================
// InlineCampaignImporter ahora se importa desde components/campaigns/InlineCampaignImporter.jsx
// ==========================================

// 5. MODIFICAR LA FUNCIÓN renderCampaigns() - REEMPLAZAR TODO EL CONTENIDO
const renderCampaigns = () => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'sending': return 'bg-blue-100 text-blue-700';
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'paused': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    if (window.confirm('¿Estás seguro de pausar esta campaña? Se detendrá en el mensaje actual.')) {
      try {
        // Pause in PHP backend
        const phpResponse = await fetch('/sistemasms/backend/api/campaigns/pause.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ campaign_id: campaignId })
        });
        
        if (!phpResponse.ok) throw new Error('Error al pausar la campaña en el backend');

        // Pause in WhatsApp server (puerto del servidor WhatsApp)
        const whatsappResponse = await fetch(`https://whatsapms.duckdns.org:3001/pause-campaign/${campaignId}`, {
          method: 'POST'
        });

        if (!whatsappResponse.ok) throw new Error('Error al pausar la campaña en el servidor de WhatsApp');
        
        // Actualizar estado local
        setCampaignsList(prev => 
          prev.map(c => 
            c.id === campaignId 
              ? { ...c, status: 'paused' }
              : c
          )
        );
        
        alert('Campaña pausada exitosamente');
      } catch (error) {
        console.error('Error:', error);
        alert('Error al pausar la campaña: ' + error.message);
      }
    }
  };

  // Nota: la reanudación se realiza mediante el workflow de "Reciclar" en la app.
  // Por simplicidad y según la petición, hemos eliminado la función específica
  // de reanudar que utilizaba `resume.php` para evitar duplicar mecanismos.

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completada';
      case 'sending': return 'Enviando';
      case 'draft': return 'Borrador';
      case 'paused': return 'Pausada';
      default: return 'Desconocido';
    }
  };

  // 🆕 APLICAR FILTRO DE EMPRESA
  const campañasFiltradas = filtrarCampañasPorEmpresa(campaignsList);

  // 🆕 CONTAR CAMPAÑAS POR EMPRESA
  const contarPorEmpresa = () => {
    const conteos = {
      all: campaignsList.length,
      'sin-empresa': campaignsList.filter(c => !c.empresa_id).length
    };
    
    empresasParaFiltro.forEach(empresa => {
      conteos[empresa.id] = campaignsList.filter(c => c.empresa_id === empresa.id).length;
    });
    
    return conteos;
  };

  const conteosPorEmpresa = contarPorEmpresa();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Gestión de Campañas</h1>
          <p className="text-gray-600 mt-1">
            {campañasFiltradas.length} de {campaignsList.length} campañas 
            {filtroEmpresa !== 'all' && ` • Filtrado por: ${
              filtroEmpresa === 'sin-empresa' ? 'Sin Empresa' : 
              obtenerNombreEmpresa(parseInt(filtroEmpresa))
            }`}
          </p>
          
          {/* 🧪 BOTÓN DE DEBUG TEMPORAL */}

        </div>
        
        {/* 🔧 AQUÍ VA TU NUEVA SECCIÓN DE BOTONES */}
        <div className="flex items-center gap-3">
          {/* Filtro por empresa */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Empresa:</label>
            <select
              value={filtroEmpresa}
              onChange={(e) => setFiltroEmpresa(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="all">🏢 Todas ({conteosPorEmpresa.all})</option>
              <option value="sin-empresa">❌ Sin empresa ({conteosPorEmpresa['sin-empresa']})</option>
              {empresasParaFiltro.map(empresa => (
                <option key={empresa.id} value={empresa.id}>
                  🏢 {empresa.empresa_name} ({conteosPorEmpresa[empresa.id] || 0})
                </option>
              ))}
            </select>
          </div>
          
          {/* Botones de gestión de empresas */}
          <button
            onClick={crearEmpresaSimple}
            className="flex items-center gap-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            title="Crear nueva empresa"
          >
            ➕ Nueva Empresa
          </button>
          
          {/* Botón eliminar empresa (solo si hay una seleccionada) */}
          {filtroEmpresa !== 'all' && filtroEmpresa !== 'sin-empresa' && (
            <button
              onClick={() => {
                const empresa = empresasParaFiltro.find(e => e.id == filtroEmpresa);
                if (empresa) {
                  eliminarEmpresaSimple(empresa.id, empresa.empresa_name);
                }
              }}
              className="flex items-center gap-1 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
              title="Eliminar empresa seleccionada"
            >
              🗑️ Eliminar {empresasParaFiltro.find(e => e.id == filtroEmpresa)?.empresa_name}
            </button>
          )}
          
          <button 
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Send size={18} />
            Nueva Campaña
          </button>
        </div>
      </div>

      

        {/* MOSTRAR FORMULARIO DE CREACIÓN */}
        {showCreateForm && (
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
          <CampaignCreator 
            onCampaignCreated={async (newCampaign) => {
              setCampaignsList([newCampaign, ...campaignsList]);
              setShowCreateForm(false);
              
              try {
                const response = await campaignsAPI.list({ userId: 1 });
                setCampaignsList(response.data.data.campaigns || []);
              } catch (error) {
                console.error('Error recargando campañas:', error);
              }
              
              alert('🎉 Campaña creada exitosamente con variables personalizables!');
            }}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      {/* 🆕 MENSAJE CUANDO NO HAY CAMPAÑAS FILTRADAS */}
      {campañasFiltradas.length === 0 && campaignsList.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <div className="text-4xl mb-3">🔍</div>
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">
            No hay campañas para esta empresa
          </h3>
          <p className="text-yellow-700">
            {filtroEmpresa === 'sin-empresa' 
              ? 'No hay campañas sin empresa asignada'
              : `No hay campañas para ${obtenerNombreEmpresa(parseInt(filtroEmpresa))}`
            }
          </p>
          <button
            onClick={() => setFiltroEmpresa('all')}
            className="mt-3 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            Ver todas las campañas
          </button>
        </div>
      )}

      {/* TABLA DE CAMPAÑAS */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            Campañas 
            {filtroEmpresa !== 'all' && (
              <span className="text-blue-600">
                - {filtroEmpresa === 'sin-empresa' ? 'Sin Empresa' : obtenerNombreEmpresa(parseInt(filtroEmpresa))}
              </span>
            )}
          </h3>
        </div>

        {campañasFiltradas.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-6 font-semibold text-gray-700">Campaña</th>
                  <th className="text-left py-3 px-6 font-semibold text-gray-700">🏢 Empresa</th>
                  <th className="text-left py-3 px-6 font-semibold text-gray-700">Estado</th>
                  <th className="text-left py-3 px-6 font-semibold text-gray-700">Progreso</th>
                  <th className="text-left py-3 px-6 font-semibold text-gray-700">Fecha</th>
                  <th className="text-left py-3 px-6 font-semibold text-gray-700">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {campañasFiltradas.map((campaign) => (
                  <React.Fragment key={campaign.id}>
                    {/* FILA PRINCIPAL DE LA CAMPAÑA */}
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div>
                          <h4 className="font-semibold text-gray-800">{campaign.campaign_name}</h4>
                          <p className="text-sm text-gray-500">ID: {campaign.id}</p>
                        </div>
                      </td>
                      
                      {/* 🆕 COLUMNA DE EMPRESA */}
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${
                            campaign.empresa_id === 1 ? 'bg-red-500' :      // Claro - Rojo
                            campaign.empresa_id === 2 ? 'bg-purple-500' :   // Entel - Morado
                            campaign.empresa_id === 3 ? 'bg-blue-500' :     // Movistar - Azul
                            campaign.empresa_id === 4 ? 'bg-green-500' :    // WOM - Verde
                            'bg-gray-400'                                     // Sin empresa - Gris
                          }`}></div>
                          <span className={`text-sm font-medium ${
                            campaign.empresa_id === 1 ? 'text-red-700' :
                            campaign.empresa_id === 2 ? 'text-purple-700' :
                            campaign.empresa_id === 3 ? 'text-blue-700' :
                            campaign.empresa_id === 4 ? 'text-green-700' :
                            'text-gray-600'
                          }`}>
                            {obtenerNombreEmpresa(campaign.empresa_id)}
                          </span>
                        </div>
                      </td>

                      <td className="py-4 px-6">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}>
                          {getStatusText(campaign.status)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div>
                          <div className="text-sm font-medium text-gray-700">
                            {campaign.messages_sent} / {campaign.total_recipients}
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ 
                                width: campaign.total_recipients > 0 
                                  ? `${(campaign.messages_sent / campaign.total_recipients) * 100}%` 
                                  : '0%' 
                              }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {campaign.created_at}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex gap-2 flex-wrap">
                          {campaign.status === 'draft' && (
                            <button
                              onClick={() => sendCampaignImmediately(campaign.id, campaign.campaign_name)}
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                            >
                              Enviar
                            </button>
                          )}
                          

                          


                          {/* 🔄 BOTÓN IMPORTAR */}
                          <button
                            onClick={() => handleImportClick(campaign)}
                            className={`px-3 py-1 rounded text-sm transition-colors font-medium ${
                              campaignWithImportOpen === campaign.id
                                ? 'bg-purple-600 text-white'
                                : 'bg-purple-200 text-purple-800 hover:bg-purple-300'
                            }`}
                            title="Importar contactos a esta campaña"
                          >
                            👥 {campaignWithImportOpen === campaign.id ? 'Cerrar' : 'Importar'}
                          </button>


                          {/* ⏸️ BOTÓN PAUSAR */}
                          {campaign.status === 'sending' && (
                            <>
                              <button onClick={() => handlePauseCampaign(campaign.id)} className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700 transition-colors">
                                ⏸️ Pausar
                              </button>
                              <div className="text-xs text-gray-600 ml-2 mt-1">
                                Pendientes: {Math.max((campaign.total_recipients || 0) - (campaign.messages_sent || 0), 0)}
                              </div>
                            </>
                          )}

                          {/* � BOTÓN VERIFICAR NÚMEROS */}
                          <button 
                            onClick={async (event) => {
                              if (!window.confirm(`¿Verificar todos los números de "${campaign.campaign_name}"?\n\nEsto revisará qué números no se entregaron desde WhatsApp.`)) return;
                              
                              try {
                                // Mostrar loading en el botón
                                const button = event.target;
                                const originalText = button.textContent;
                                button.textContent = '⏳ Verificando...';
                                button.disabled = true;
                                
                                const response = await fetch('/sistemasms/backend/api/whatsapp/verify-numbers.php', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify({ campaign_id: campaign.id })
                                });
                                const result = await response.json();
                                
                                // Restaurar botón
                                button.textContent = originalText;
                                button.disabled = false;
                                
                                if (result.success) {
                                  let mensaje = `✅ Verificación completada para "${campaign.campaign_name}"!\n\n`;
                                  mensaje += `📊 RESUMEN:\n`;
                                  mensaje += `• Total enviados: ${result.statistics.total_sent}\n`;
                                  mensaje += `• Entregados: ${result.statistics.delivered}\n`;
                                  mensaje += `• Números inválidos: ${result.statistics.invalid_numbers}\n`;
                                  mensaje += `• Pendientes: ${result.statistics.pending}\n`;
                                  mensaje += `• Tasa de entrega: ${result.summary.delivery_rate}%\n`;
                                  
                                  if (result.invalid_numbers && result.invalid_numbers.length > 0) {
                                    mensaje += `\n❌ NÚMEROS INVÁLIDOS DETECTADOS:\n`;
                                    result.invalid_numbers.forEach(invalid => {
                                      mensaje += `• ${invalid.phone} (${invalid.name})\n`;
                                    });
                                    mensaje += `\nEstos números se marcaron como inválidos en la base de datos.`;
                                  } else {
                                    mensaje += `\n✅ No se detectaron números inválidos nuevos.`;
                                  }
                                  
                                  alert(mensaje);
                                }
                              } catch (error) {
                                // Restaurar botón en caso de error
                                const button = event.target;
                                button.textContent = '📱 Verificar Números';
                                button.disabled = false;
                                
                                alert('❌ Error en verificación: ' + error.message);
                              }
                            }}
                            style={{
                              backgroundColor: '#9333ea', // purple-600
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              fontWeight: '500',
                              border: 'none',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#7c3aed'} // purple-700
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#9333ea'} // purple-600
                          >
                            📱 Verificar Números
                          </button>

                          {/* �📊 BOTÓN EXCEL */}
                          <button
                            onClick={async () => {
                              try {
                                const response = await fetch(`/sistemasms/backend/api/campaigns/export-with-whatsapp.php?campaign_id=${campaign.id}`, {
                                  credentials: 'include'
                                });
                                
                                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                                
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `WhatsApp_${campaign.campaign_name.replace(/[^A-Za-z0-9_-]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                window.URL.revokeObjectURL(url);
                                
                                alert('✅ Excel con WhatsApp exportado exitosamente!');
                              } catch (error) {
                                alert('❌ Error al exportar: ' + error.message);
                              }
                            }}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                          >
                            📊 Excel
                          </button>


                          {/* 🆕 BOTÓN RECICLAR CAMPAÑA */}
                          {campaign.status !== 'completed' && (campaign.total_recipients > (campaign.messages_sent || 0) || campaign.status === 'sending') && (
                            <button
                              onClick={async () => {
                                const remaining = campaign.total_recipients - (campaign.messages_sent || 0);
                                if (window.confirm(`¿Deseas reciclar la campaña "${campaign.campaign_name}"?\n\n📊 Estado actual: ${campaign.messages_sent || 0}/${campaign.total_recipients}\n🔄 Se reintentarán ${remaining} contactos que no se entregaron.\n\nEsto reactivará la campaña y continuará desde donde quedó.`)) {
                                  try {
                                    console.log('♻️ Reciclando campaña:', campaign.id);

                                    let isMeta = false;
                                    try {
                                      const content = JSON.parse(campaign.message_content);
                                      isMeta = content.is_meta_template === true;
                                    } catch(e){}

                                    const endpoint = isMeta 
                                      ? '/sistemasms/backend/api/whatsapp/recycle-meta.php'
                                      : '/sistemasms/backend/api/campaigns/recycle-campaign.php';

                                    const response = await fetch(endpoint, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      credentials: 'include',
                                      body: JSON.stringify({ campaign_id: campaign.id })
                                    });

                                    const result = await response.json();

                                    if (result.success) {
                                        // Soporte para distintas formas de respuesta del backend.
                                        // Algunos endpoints devuelven result.data, otros devuelven campos en root.
                                        const recycleData = result.data ?? result;

                                        // Si el backend indica explícitamente 'action' === 'reactivated', o bien
                                        // si hay contactos procesados/por reintentar, tratamos como reactivación.
                                        const wasReactivated = (recycleData && (
                                          recycleData.action === 'reactivated' ||
                                          (typeof recycleData.contacts_to_retry !== 'undefined' && recycleData.contacts_to_retry > 0) ||
                                          (typeof recycleData.contacts_processed !== 'undefined' && recycleData.contacts_processed > 0) ||
                                          (typeof recycleData.total_contacts !== 'undefined' && recycleData.total_contacts > 0)
                                        ));

                                        if (wasReactivated) {
                                          alert(`✅ Campaña reciclada exitosamente!\n\n♻️ Campaña reactivada:\n"${recycleData.campaign_name || recycleData.campaign_name || ''}"\n\n📊 Contactos para reintentar: ${recycleData.contacts_to_retry ?? recycleData.contacts_processed ?? recycleData.total_contacts ?? 0}\n\n¡La campaña continuará enviando desde donde quedó!`);

                                          // 🔄 Disparar el envío inmediato usando el endpoint correspondiente
                                          try {
                                            const sendEndpoint = isMeta
                                              ? '/sistemasms/backend/api/whatsapp/send-campaign.php'
                                              : '/sistemasms/backend/api/campaigns/send-immediately.php';

                                            const sendResp = await fetch(sendEndpoint, {
                                              method: 'POST',
                                              credentials: 'include',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({ campaign_id: campaign.id })
                                            });
                                            const sendResult = await sendResp.json();

                                            if (sendResp.ok && sendResult.success) {
                                              // Actualizar estado solo tras confirmación del servidor
                                              setCampaignsList(prev => prev.map(c => (
                                                c.id === campaign.id ? { ...c, status: 'sending' } : c
                                              )));
                                            } else if (sendResult?.error === 'campaign_paused' || sendResp.status === 409) {
                                              alert('No se pudo iniciar el envío: la campaña está pausada.');
                                            } else {
                                              console.error('Error iniciando envío tras reciclar:', sendResult);
                                            }
                                          } catch (e) {
                                            console.error('Error disparando envío después de reciclar:', e);
                                          }
                                        } else {
                                          // Fallback: reciclaje por nueva campaña o respuesta sin detalles
                                          alert(`✅ Campaña reciclada exitosamente!\n\n📋 Resultado:\n"${recycleData.new_campaign_name || recycleData.campaign_name || recycleData.message || 'Sin detalles'}"\n\n📊 Contactos reciclados: ${recycleData.contacts_to_retry ?? recycleData.contacts_processed ?? recycleData.total_contacts ?? 0}`);
                                        }
                                      // Recargar campañas para ver la nueva campaña/estado
                                      const fetchCampaigns = async () => {
                                        try {
                                          const response = await campaignsAPI.list({ userId: 1 });
                                          setCampaignsList(prev => mergeCampaignsPreservePaused(prev, response.data.data.campaigns || []));
                                        } catch (error) {
                                          console.error('Error recargando campañas:', error);
                                        }
                                      };
                                      fetchCampaigns();
                                    } else {
                                      throw new Error(result.message || 'Error reciclando campaña');
                                    }
                                  } catch (error) {
                                    alert('❌ Error reciclando campaña: ' + error.message);
                                  }
                                }
                              }}
                              className="bg-green-100 text-green-700 px-3 py-1 rounded text-sm hover:bg-green-200 transition-colors"
                              title="Reactivar campaña y reintentar contactos no entregados"
                            >
                              ♻️ Reciclar
                            </button>
                          )}

                          {/* 🗑️ BOTÓN ELIMINAR - Solo para administradores */}
                          {canDeleteCampaign() && (
                            <button
                              onClick={async () => {
                                if (window.confirm(`¿Estás seguro de eliminar la campaña "${campaign.campaign_name}"?\n\nEsto eliminará:\n• La campaña\n• Todos sus contactos\n• Todos los registros de envío\n\nEsta acción NO se puede deshacer.`)) {
                                  try {
                                    const response = await fetch(`/sistemasms/backend/api/campaigns/delete-campaign.php?campaign_id=${campaign.id}`, {
                                      method: 'DELETE',
                                      credentials: 'include'
                                    });

                                    const result = await response.json();

                                    if (result.success) {
                                      alert(`✅ ${result.message}`);
                                      // Remover de la lista local
                                      setCampaignsList(prev => prev.filter(c => c.id !== campaign.id));
                                    } else {
                                      throw new Error(result.message);
                                    }
                                  } catch (error) {
                                    alert('❌ Error eliminando campaña: ' + error.message);
                                  }
                                }
                              }}
                              className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                              title="Eliminar campaña permanentemente"
                            >
                              <Trash2 size={14} className="mr-1" />
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* FILA EXPANDIBLE DE IMPORTACIÓN */}
                    {campaignWithImportOpen === campaign.id && (
                      <InlineCampaignImporter 
                        campaign={campaign}
                        onClose={() => setCampaignWithImportOpen(null)}
                        onSuccess={handleImportSuccess}
                        setImportingFile={setImportingFile}
                      />
                    )}


                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : campaignsList.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Send size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No hay campañas creadas</p>
            <p className="text-sm">Crea tu primera campaña para comenzar</p>
          </div>
        ) : null}
      </div>

      {/* ESTADÍSTICAS POR EMPRESA */}
      {campaignsList.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-100">
                <Send className="text-blue-600" size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-800">{campaignsList.length}</h3>
                <p className="text-gray-600 font-medium">Total Campañas</p>
              </div>
            </div>
          </div>

          {/* 🆕 ESTADÍSTICAS POR EMPRESA */}
          {empresasParaFiltro.slice(0, 3).map(empresa => {
            const campanasEmpresa = campaignsList.filter(c => c.empresa_id === empresa.id);
            const completadas = campanasEmpresa.filter(c => c.status === 'completed').length;
            
            return (
              <div key={empresa.id} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${
                    empresa.id === 1 ? 'bg-red-100' :
                    empresa.id === 2 ? 'bg-purple-100' :
                    empresa.id === 3 ? 'bg-blue-100' :
                    'bg-green-100'
                  }`}>
                    <div className={`w-6 h-6 rounded ${
                      empresa.id === 1 ? 'bg-red-500' :
                      empresa.id === 2 ? 'bg-purple-500' :
                      empresa.id === 3 ? 'bg-blue-500' :
                      'bg-green-500'
                    }`}></div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800">{campanasEmpresa.length}</h3>
                    <p className="text-gray-600 font-medium">{empresa.empresa_name}</p>
                    <p className="text-xs text-gray-400">{completadas} completadas</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


  const renderAnalytics = () => {
    if (analyticsLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando analíticas...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-800">Analíticas</h1>
          <div className="flex gap-3">
            <select
              value={analyticsPeriod}
              onChange={(e) => setAnalyticsPeriod(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7">Últimos 7 días</option>
              <option value="30">Últimos 30 días</option>
              <option value="90">Últimos 90 días</option>
            </select>
          </div>
        </div>

        {analyticsData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard 
                icon={Send}
                title="Campañas Creadas"
                value={analyticsData.period_stats?.total_campaigns || 0}
                subtitle={`Últimos ${analyticsPeriod} días`}
                color="blue"
              />
              <StatCard 
                icon={Users}
                title="Destinatarios"
                value={(analyticsData.period_stats?.total_recipients || 0).toLocaleString()}
                subtitle="Total del período"
                color="green"
              />
              <StatCard 
                icon={MessageSquare}
                title="Mensajes Enviados"
                value={(analyticsData.period_stats?.total_messages_sent || 0).toLocaleString()}
                subtitle="Del período"
                color="purple"
              />
              <StatCard 
                icon={TrendingUp}
                title="Tasa de Entrega"
                value={`${analyticsData.period_stats?.avg_delivery_rate || 0}%`}
                subtitle="Promedio"
                color="orange"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Mensajes por Día</h3>
                {analyticsData.messages_by_day?.length > 0 ? (
                  <div className="space-y-3">
                    {analyticsData.messages_by_day.map((day, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-800">{day.date}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold text-blue-600">{day.messages_sent}</span>
                          <div className="text-sm text-gray-500">
                            {day.delivered} entregados | {day.failed} fallidos
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">No hay datos para mostrar</div>
                )}
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Campañas por Estado</h3>
                {analyticsData.campaigns_by_status?.length > 0 ? (
                  <div className="space-y-3">
                    {analyticsData.campaigns_by_status.map((status, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            status.status === 'completed' ? 'bg-green-500' :
                            status.status === 'sending' ? 'bg-blue-500' :
                            status.status === 'draft' ? 'bg-gray-500' :
                            'bg-yellow-500'
                          }`}></div>
                          <span className="font-medium text-gray-800 capitalize">{status.status}</span>
                        </div>
                        <span className="text-lg font-bold text-gray-800">{status.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">No hay datos para mostrar</div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Top Campañas</h3>
                {analyticsData.top_campaigns?.length > 0 ? (
                  <div className="space-y-3">
                    {analyticsData.top_campaigns.map((campaign, index) => (
                      <div key={index} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-800">{campaign.campaign_name}</h4>
                          <span className="text-green-600 font-bold">{campaign.delivery_rate}%</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {campaign.delivered_count}/{campaign.total_recipients} entregados
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ width: `${campaign.delivery_rate}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">No hay campañas completadas</div>
                )}
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Contactos por Grupo</h3>
                {analyticsData.contacts_by_group?.length > 0 ? (
                  <div className="space-y-3">
                    {analyticsData.contacts_by_group.map((group, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium text-gray-800">{group.group_name}</span>
                        <span className="text-lg font-bold text-blue-600">{group.contact_count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">No hay contactos organizados</div>
                )}
              </div>
            </div>

            {analyticsData.hourly_activity?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Actividad por Horas</h3>
                <div className="grid grid-cols-12 gap-2">
                  {Array.from({ length: 24 }, (_, hour) => {
                    const hourData = analyticsData.hourly_activity.find(h => h.hour === hour);
                    const count = hourData ? hourData.messages_count : 0;
                    const maxCount = Math.max(...analyticsData.hourly_activity.map(h => h.messages_count));
                    const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                    
                    return (
                      <div key={hour} className="text-center">
                        <div 
                          className="bg-blue-500 rounded-t mx-auto mb-1"
                          style={{ 
                            height: `${Math.max(height, 4)}px`,
                            width: '20px',
                            minHeight: '4px'
                          }}
                          title={`${hour}:00 - ${count} mensajes`}
                        ></div>
                        <div className="text-xs text-gray-600">{hour}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderSettings = () => {
    if (settingsLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando configuración...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-800">Configuración</h1>
        </div>

        {settingsData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                      <User className="text-white" size={32} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">Perfil de Usuario</h3>
                      <p className="text-gray-600">Gestiona tu información personal</p>
                    </div>
                  </div>
                  {!editingProfile && (
                    <button 
                      onClick={() => setEditingProfile(true)}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Edit size={16} />
                      Editar
                    </button>
                  )}
                </div>

                {editingProfile ? (
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Completo
                      </label>
                      <input
                        type="text"
                        value={profileForm.full_name || ''}
                        onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={profileForm.email || ''}
                        onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Usuario
                      </label>
                      <input
                        type="text"
                        value={profileForm.username || ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                        disabled
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Save size={16} />
                        Guardar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingProfile(false);
                          setProfileForm(settingsData.user_data);
                        }}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Nombre Completo</label>
                      <p className="text-lg text-gray-800">{settingsData.user_data?.full_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-lg text-gray-800">{settingsData.user_data?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Usuario</label>
                      <p className="text-lg text-gray-800">{settingsData.user_data?.username || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Miembro desde</label>
                      <p className="text-lg text-gray-800">
                        {settingsData.user_data?.created_at ? 
                          new Date(settingsData.user_data.created_at).toLocaleDateString() : 
                          'N/A'
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Shield className="text-red-600" size={24} />
                    <h3 className="text-xl font-bold text-gray-800">Seguridad</h3>
                  </div>
                  {!showPasswordForm && (
                    <button
                      onClick={() => setShowPasswordForm(true)}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Cambiar Contraseña
                    </button>
                  )}
                </div>

                {showPasswordForm ? (
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contraseña Actual
                      </label>
                      <input
                        type="password"
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nueva Contraseña
                      </label>
                      <input
                        type="password"
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmar Nueva Contraseña
                      </label>
                      <input
                        type="password"
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Cambiar Contraseña
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                        }}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                ) : (
                  <p className="text-gray-600">Mantén tu cuenta segura cambiando tu contraseña regularmente.</p>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <Database className="text-blue-600" size={24} />
                  <h3 className="text-lg font-bold text-gray-800">Estadísticas</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Contactos</span>
                    <span className="font-bold text-blue-600">{settingsData.user_stats?.total_contacts || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Campañas</span>
                    <span className="font-bold text-green-600">{settingsData.user_stats?.total_campaigns || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mensajes</span>
                    <span className="font-bold text-purple-600">{settingsData.user_stats?.total_messages || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Grupos</span>
                    <span className="font-bold text-orange-600">{settingsData.user_stats?.total_groups || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Grupos de Contactos</h3>
                  <button
                    onClick={() => setShowNewGroupForm(true)}
                    className="flex items-center gap-2 bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    <Plus size={16} />
                    Nuevo
                  </button>
                </div>

                {showNewGroupForm && (
                  <form onSubmit={handleCreateGroup} className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Nombre del grupo"
                        value={newGroupForm.group_name}
                        onChange={(e) => setNewGroupForm({...newGroupForm, group_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        required
                      />
                      <input
                        type="text"
                        placeholder="Descripción (opcional)"
                        value={newGroupForm.description}
                        onChange={(e) => setNewGroupForm({...newGroupForm, description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                        >
                          Crear
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewGroupForm(false);
                            setNewGroupForm({ group_name: '', description: '' });
                          }}
                          className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-400 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </form>
                )}

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {settingsData.contact_groups?.length > 0 ? (
                    settingsData.contact_groups.map((group) => (
                      <div key={group.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-800">{group.group_name}</h4>
                          <p className="text-sm text-gray-600">{group.total_contacts} contactos</p>
                        </div>
                        <button
                          onClick={() => handleDeleteGroup(group.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          title="Eliminar grupo"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">No hay grupos creados</div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Configuraciones del Sistema</h3>
                <div className="space-y-4">
                  {settingsData.settings && Object.entries(settingsData.settings).map(([key, setting]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-800">{setting.description}</h4>
                        <p className="text-sm text-gray-500">{key}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer" 
                          defaultChecked={setting.value === '1'}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    // Mostrar el panel de bienvenida solo cuando no hay pestaña activa
    if (!activeTab && userRole === 'ejecutivo') {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">📱 Panel de Ejecutivo</h2>
            <p className="text-gray-600 mb-4">Bienvenido al sistema de envío masivo para ejecutivos.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-800">Envío Masivo</h3>
                <p className="text-blue-600 text-sm">Enviar mensajes a múltiples contactos</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800">Campañas</h3>
                <p className="text-green-600 text-sm">Gestionar campañas de WhatsApp</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-800">Configuración</h3>
                <p className="text-purple-600 text-sm">Configurar el sistema</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Envío Masivo WhatsApp</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600">Funcionalidad de envío masivo disponible para ejecutivos.</p>
                <p className="text-sm text-gray-500 mt-2">Contactos pre-configurados: 3 contactos listos para envío</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'bulk-sender':
        return hasPermission('bulk-sender') ? <BulkWhatsAppSender /> : <div className="p-8 text-center text-gray-500">No tienes permisos para acceder a esta sección</div>;
      case 'dashboard':
        return hasPermission('dashboard') ? renderDashboard() : <div className="p-8 text-center text-gray-500">No tienes permisos para acceder a esta sección</div>;
      case 'contacts':
        return hasPermission('contacts') ? renderContacts() : <div className="p-8 text-center text-gray-500">No tienes permisos para acceder a esta sección</div>;
      case 'campaigns':
        return hasPermission('campaigns') ? renderCampaigns() : <div className="p-8 text-center text-gray-500">No tienes permisos para acceder a esta sección</div>;
      case 'analytics':
        return hasPermission('analytics') ? renderAnalytics() : <div className="p-8 text-center text-gray-500">No tienes permisos para acceder a esta sección</div>;
      case 'settings':
        return hasPermission('settings') ? renderSettings() : <div className="p-8 text-center text-gray-500">No tienes permisos para acceder a esta sección</div>;
      default:
        console.log(`❌ Unknown or forbidden tab: ${activeTab}`);
        return <div className="p-8 text-center text-gray-500">Sección no encontrada</div>;
    }
  };

  // Si no está autenticado, mostrar el componente de login
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header style={{ 
        backgroundColor: 'var(--header-bg)', 
        borderBottom: '1px solid var(--header-border)',
        backdropFilter: 'blur(12px)',
        position: 'sticky',
        top: 0,
        zIndex: 40
      }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div style={{ 
                background: 'var(--accent-gradient)', 
                borderRadius: 'var(--radius-lg)',
                width: '2.5rem',
                height: '2.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <MessageSquare className="text-white" size={22} />
              </div>
              <h1 style={{ 
                fontSize: '1.5rem', 
                fontWeight: 800, 
                background: 'var(--accent-gradient)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                letterSpacing: '-0.025em'
              }}>
                MessageHub
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                  {currentUser?.full_name || 'Usuario'}
                </p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {currentUser?.email || ''} • {currentUser?.role === 'admin' ? 'Administrador' : 'Ejecutivo'}
                </p>
              </div>
              <div style={{
                width: '2.25rem',
                height: '2.25rem',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: currentUser?.role === 'admin' ? 'var(--danger)' : 'var(--accent-primary)',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#fff'
              }}>
                {currentUser?.full_name?.charAt(0) || currentUser?.username?.charAt(0) || 'U'}
              </div>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="theme-toggle"
                title={darkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              >
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button
                onClick={handleLogout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  color: 'var(--text-tertiary)',
                  borderRadius: 'var(--radius-md)',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--text-tertiary)';
                }}
                title="Cerrar sesión"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>


      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          <div style={{ 
            width: '16rem', 
            backgroundColor: 'var(--sidebar-bg)', 
            borderRadius: 'var(--radius-xl)', 
            boxShadow: 'var(--shadow-lg)', 
            padding: '1.5rem', 
            height: 'fit-content',
            border: '1px solid var(--sidebar-border)',
            position: 'sticky',
            top: '5rem'
          }}>

            <div className="space-y-2">
              {getAllowedTabs().map(tab => (
                <MenuItem
                  key={tab.key}
                  icon={tab.icon}
                  label={tab.label}
                  tabKey={tab.key}
                  count={tab.count || (tab.key === 'contacts' ? stats.total_contacts : tab.key === 'campaigns' ? stats.active_campaigns : undefined)}
                />
              ))}
            </div>
          </div>

          <div className="flex-1">
            {renderContent()}
          </div>
        </div>
        {showCampaignTracker && selectedCampaign && (
          <CampaignTracker 
            campaign={selectedCampaign}
            onClose={() => {
            setShowCampaignTracker(false);
            setSelectedCampaign(null);
          }}
          />
        )}
        {showImportModal && selectedCampaignForImport && (
          <CampaignImportModal
            isOpen={showImportModal}
            onClose={() => {
              setShowImportModal(false);
              setSelectedCampaignForImport(null);
            }}
            campaignId={selectedCampaignForImport.id}
            campaignName={selectedCampaignForImport.name}
            onImportSuccess={async (importData) => {
              console.log('✅ Importación completada:', importData);
              alert(`Importación exitosa: ${importData.total_imported || importData.successful_imports} contactos importados`);
              
              // ✅ RECARGAR LISTA DE CAMPAÑAS DESPUÉS DE IMPORTAR
              try {
                console.log('🔄 Refrescando lista de campañas...');
                const response = await campaignsAPI.list({ userId: 1 });
                setCampaignsList(response.data.data.campaigns || []);
                console.log('✅ Lista actualizada');
              } catch (error) {
                console.error('Error refrescando campañas:', error);
              }
              
              setShowImportModal(false);
              setSelectedCampaignForImport(null);
            }}
         />
        )}


        {/* 🆕 MODAL IMPORTAR KPI CANCELACIONES */}
        {showKpiImport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Importar Cancelaciones</h3>
                <button
                  onClick={() => setShowKpiImport(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Empresa (opcional)
                </label>
                <select
                  id="kpi-import-empresa"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  defaultValue="all"
                >
                  <option value="all">Sin asignar empresa</option>
                  {kpiData?.empresas?.map(empresa => (
                    <option key={empresa.id} value={empresa.id}>
                      {empresa.empresa_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Puedes asociar estas cancelaciones a una empresa específica
                </p>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Sube un archivo Excel (.xlsx) con las siguientes columnas:
                </p>
                <ul className="text-sm text-gray-700 space-y-2 bg-gray-50 p-4 rounded-lg">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-semibold">•</span>
                    <span><strong>RUT:</strong> Identificador del cliente (requerido)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-semibold">•</span>
                    <span><strong>Nombre:</strong> Nombre del cliente (opcional)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-semibold">•</span>
                    <span><strong>Monto Pagado:</strong> Valor numérico (requerido)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-semibold">•</span>
                    <span><strong>Fecha de Cancelación:</strong> DD/MM/YYYY o YYYY-MM-DD (requerido)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-semibold">•</span>
                    <span><strong>Motivo:</strong> Razón de cancelación (opcional)</span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seleccionar archivo
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const empresaSelect = document.getElementById('kpi-import-empresa');
                      const empresaId = empresaSelect.value;
                      handleKpiImport(e.target.files[0], empresaId);
                    }
                  }}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-red-50 file:text-red-700
                    hover:file:bg-red-100
                    cursor-pointer"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowKpiImport(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};



export default MessageHub;
